import React from 'react';
import { Stack, Typography, Link, SxProps, Theme } from '@mui/material';

import { getRegionByCountryCode, UserRegion } from '/packages/shared-components/utils/countryCodeToRegion';
import { TBuildResult } from '../MagicForm/MagicForm';

const FooterLinks = ({ label, links }: { label: string | null; links: { href: string; label: string }[] }) => {
  return (
    <Typography
      variant="subtitle1"
      component={'span'}
      color="text.secondary"
      sx={{ lineHeight: 1 }}
      data-cy="footer-region"
    >
      {label ? `${label}: ` : ''}
      {links.map((link, i) => (
        <React.Fragment key={`${label && label.trim()}-link-${i}`}>
          {i !== 0 && ' • '}
          <Typography variant="body1" component="span" color="info.main">
            <Link href={link.href} target="_blank" rel="noreferrer" underline="hover" data-cy="region-links">
              {link.label}
            </Link>
          </Typography>
        </React.Fragment>
      ))}
    </Typography>
  );
};

export interface FooterProps {
  displaySize?: 'small' | 'responsive';
  sx?: SxProps<Theme>;
  countryCode: string | null;
  build?: TBuildResult;
}

export const Footer = ({ displaySize = 'responsive', sx, countryCode, build }: FooterProps) => {
  const region: UserRegion | null = countryCode ? getRegionByCountryCode(countryCode) : null;

  const getFoorterLinks = (region: UserRegion | null) => {
    switch (region) {
      case UserRegion.USA:
        return [
          { label: 'Privacy Policy', href: 'https://sites.disney.com/ngr-privacy-policy/' },
          {
            label: 'California Notice at Collection',
            href: 'https://sites.disney.com/ngr-privacy-policy/#cali-notice-at-collection',
          },
        ];
      case UserRegion.APAC:
        return [{ label: 'Privacy Policy', href: 'https://sites.disney.com/ngr-privacy-policy/' }];
      case UserRegion.EMEA:
        return [
          { label: 'Privacy Policy', href: 'https://sites.disney.com/ngr-privacy-policy/' },
          {
            label: 'Cookies Policy',
            href: 'https://privacy.thewaltdisneycompany.com/en/current-privacy-policy/cookies-policy/',
          },
        ];
      case UserRegion.LATAM:
        return [{ label: 'Privacy Policy', href: 'https://sites.disney.com/ngr-privacy-policy/' }];
      default:
        return [{ label: 'Privacy Policy', href: 'https://sites.disney.com/ngr-privacy-policy/' }];
    }
  };

  return (
    <footer>
      <Stack
        flexDirection={displaySize === 'small' ? 'column' : { xs: 'column', sm: 'row' }}
        rowGap={6}
        columnGap={2}
        justifyContent="center"
        alignItems="center"
        py={displaySize === 'small' ? 4 : { xs: 4, sm: 3 }}
        gap={displaySize === 'small' ? 1 : { xs: 1, sm: 2 }}
        sx={[
          (theme) => ({
            borderTop: `1px solid ${theme.palette.divider}`,
            textAlign: 'center',
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <FooterLinks label={region} links={getFoorterLinks(region)} />
        <Typography variant={'body1'} component={'span'} color="text.secondary" data-cy="disney-rights">
          © Disney. All Rights Reserved
        </Typography>
        {build && (
          <Typography variant={'body1'} component={'span'} color="text.secondary">
            v{build.version}
          </Typography>
        )}
      </Stack>
    </footer>
  );
};
